'use client';
import { DateTime } from 'luxon';
import React from 'react';

import ListPage from '../../../../../components/page-components/list-page/list-page';
import {
  createListCardArray,
  serializeListCardDateTimes,
} from '../../../../../helpers/list-page/list-page-helpers';
import {
  pageIsEvent,
  pageIsNewsItem,
  pageIsOffer,
} from '../../../../../helpers/list-page/type-guards';
import { IMeetingPlacePageOccations } from '@/lib/fragments/meetingPlacePageOccations';
import { IMeetingPlacePageNewsList } from '@/lib/fragments/meetingPlacePageNewsList';
import { IMeetingPlacePageEventList } from '@/lib/fragments/meetingPlacePageEventList';
import { IMeetingplacePageHomeMinifiedExtendedLogo } from '@/lib/fragments/meetingPlacePageHome-minified';
import { IMeetingPlacePageOfferItem } from '@/lib/fragments/meetingPlacePageOfferItem';
import useGetMobileAppWebViewHeader from '@/hooks/use-get-mobile-app-web-view';
import { isDateTimeInFuture } from '@/helpers/dates';
import { useTenantConfig } from '@/providers/ConfigProvider';
import { GtmPageView } from '@/components/page-components/analytics/gtm-page-view';

interface MeetingPlacePageOccasionsProps {
  metaData: any;
  node_locale: string;
  urlSubdirectory?: {
    urlSubDirectoryNews?: string;
    urlSubDirectoryOffer?: string;
    urlSubDirectoryEvent?: string;
  };
  occasionId?: string;
  occasionListData: {
    meetingPlacePageOccasions: IMeetingPlacePageOccations;
    meetingPlacePageNewsList: IMeetingPlacePageNewsList;
    meetingPlacePageEventList: IMeetingPlacePageEventList;
    meetingPlacePageHomeCollection: IMeetingplacePageHomeMinifiedExtendedLogo;
  };
}

const MeetingPlacePageOccasions = ({
  metaData,
  node_locale,
  urlSubdirectory,
  occasionListData,
}: any) => {
  const config = useTenantConfig();
  const prefix = config?.GATSBY_SITE_PATH_PREFIX || '';
  const page = occasionListData?.meetingPlacePageOccasions;
  const homepage = occasionListData?.meetingPlacePageHomeCollection;
  const timezone = homepage?.meetingPlacePage?.timezone || '';
  const selectInputName = {
    news: occasionListData?.meetingPlacePageNewsList?.name,
    events: occasionListData?.meetingPlacePageEventList?.name,
  };
  const allOccasions = page?.occasionsCollection?.items?.filter(Boolean) ?? [];

  const offers = allOccasions
    .filter((offer: any) => pageIsOffer(offer)) //Filter out only offers with type guard
    .map((offer: any) => offer as IMeetingPlacePageOfferItem) //Now, we can cast
    // .filter((offer: any) => DateTime.fromISO(offer.validTo).diffNow('day').days > 0); //Filter out those offers which validTo is before today
    .filter((offer: any) => isDateTimeInFuture(offer?.validTo, timezone)); //Filter out those offers which validTo is before today

  // const newsAndEvents = allOccasions.filter(
  //   (occasion: any) => !pageIsOffer(occasion),
  // ); //Only news and events

  const newsItems = allOccasions.filter(
    (occasion: any) =>
      !pageIsOffer(occasion) && pageIsNewsItem(occasion) && occasion?.sys?.id,
  );

  const eventItems = allOccasions.filter(
    (occasion: any) =>
      !pageIsOffer(occasion) && pageIsEvent(occasion) && occasion?.sys?.id,
  );

  // Create cards with appropriate urlSubdirectory for each type
  const offerCards = createListCardArray(
    offers,
    selectInputName,
    undefined,
    urlSubdirectory?.urlSubdirectoryOffer || undefined, // offers don't need urlSubdirectory
    node_locale,
    prefix,
  );

  const newsCards = createListCardArray(
    newsItems,
    selectInputName,
    undefined,
    urlSubdirectory?.urlSubdirectoryNews || undefined,
    node_locale,
    prefix,
  );

  const eventCards = createListCardArray(
    eventItems,
    selectInputName,
    timezone,
    urlSubdirectory?.urlSubdirectoryEvent || undefined,
    node_locale,
    prefix,
  );

  // const occasionsCardList = createListCardArray(
  //   [...offers, ...newsAndEvents],
  //   selectInputName,
  // );

  const occasionsCardList = [...offerCards, ...newsCards, ...eventCards];

  const serializedCardList = serializeListCardDateTimes(occasionsCardList);

  const isWebView = useGetMobileAppWebViewHeader();

  return (
    <>
      {/* ✅ GTM PAGE VIEW */}
      <GtmPageView
        homepage={homepage}
        language={node_locale}
        pageTitle={page?.name ?? ''}
      />

      <ListPage
        cardList={serializedCardList}
        pageName={page?.name}
        pageSlug={page?.slug}
        node_locale={node_locale}
        breadcrumbs={[page?.name]}
        homeText={homepage?.breadcrumbHomeText}
        tileColClass="grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        listType="occasion"
        isWebView={isWebView}
      />
    </>
  );
};

export default MeetingPlacePageOccasions;
